import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Cookie, X, ChevronRight, ShieldCheck } from "lucide-react";
import { useConsent } from "../context/ConsentContext";

export function CookieConsent() {
  const { decided, acceptAll, rejectAll, savePreferences } = useConsent();
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });
  // Mount-gate: de banner rendert pas ná hydratie, zodat de eerste
  // client-render exact overeenkomt met de prerendered HTML (geen mismatch).
  const [mounted, setMounted] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => { setMounted(true); }, []);

  if (!mounted || decided || pathname.startsWith("/admin")) return null;

  const onSave = () => {
    savePreferences(prefs);
  };

  return (
    <div
      data-testid="cookie-consent"
      className="fixed bottom-4 left-4 right-4 md:bottom-6 md:left-6 md:right-auto md:max-w-md z-[100] rounded-2xl border border-m-border bg-white shadow-[0_20px_60px_-15px_rgba(8,24,42,0.18)]"
    >
      {!showPrefs ? (
        <div className="p-4 md:p-6">
          <div className="flex items-start gap-2.5 mb-2 md:mb-3">
            <Cookie size={18} className="text-m-orange mt-0.5 shrink-0" />
            <h2 className="text-m-navy text-base md:text-lg font-bold leading-tight tracking-tight">Cookies & privacy</h2>
          </div>
          <p className="text-xs md:text-sm text-m-text-2 leading-relaxed mb-3.5 md:mb-5">
            Wij gebruiken essentiële cookies om de site te laten werken. Met uw toestemming gebruiken wij
            ook cookies voor analytics en marketing.{" "}
            <Link to="/privacy" className="text-m-orange font-medium hover:underline">
              Lees onze privacyverklaring
            </Link>.
          </p>
          <div className="flex flex-col gap-2">
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={rejectAll}
                data-testid="cookie-reject-all"
                className="text-sm font-semibold border border-m-border text-m-navy py-2.5 px-3 rounded-full hover:border-m-navy/40 hover:bg-m-surface transition-colors"
              >
                Alleen essentieel
              </button>
              <button
                onClick={acceptAll}
                data-testid="cookie-accept-all"
                className="text-sm font-semibold bg-m-orange text-white py-2.5 px-3 rounded-full hover:bg-m-orange-hover transition-colors"
              >
                Alles accepteren
              </button>
            </div>
            <button
              onClick={() => setShowPrefs(true)}
              data-testid="cookie-manage"
              className="text-xs text-m-text-2 py-1 hover:text-m-orange transition-colors inline-flex items-center justify-center gap-1"
            >
              Voorkeuren aanpassen <ChevronRight size={12} />
            </button>
          </div>
        </div>
      ) : (
        <div className="p-4 md:p-6" data-testid="cookie-preferences">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="flex items-start gap-3">
              <ShieldCheck size={20} className="text-m-orange mt-0.5 shrink-0" />
              <h2 className="text-m-navy text-lg font-bold leading-tight tracking-tight">Voorkeuren beheren</h2>
            </div>
            <button onClick={() => setShowPrefs(false)} className="text-m-muted hover:text-m-navy" data-testid="cookie-close-prefs">
              <X size={18} />
            </button>
          </div>

          <div className="space-y-4 mb-5">
            <ToggleRow
              label="Functioneel"
              desc="Nodig voor de werking van de website (formulier­invoer, login). Kan niet worden uitgeschakeld."
              checked
              disabled
              testid="cookie-toggle-functional"
            />
            <ToggleRow
              label="Analytics"
              desc="Google Analytics 4 — anonieme statistieken om de site te verbeteren."
              checked={prefs.analytics}
              onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
              testid="cookie-toggle-analytics"
            />
            <ToggleRow
              label="Marketing"
              desc="Meta Pixel en Google Ads — om relevantere advertenties te tonen en campagnes te meten."
              checked={prefs.marketing}
              onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
              testid="cookie-toggle-marketing"
            />
          </div>

          <button
            onClick={onSave}
            data-testid="cookie-save-prefs"
            className="w-full bg-m-orange text-white font-semibold py-2.5 px-4 rounded-full hover:bg-m-orange-hover transition-colors text-sm"
          >
            Voorkeuren opslaan
          </button>
        </div>
      )}
    </div>
  );
}

function ToggleRow({ label, desc, checked, onChange, disabled, testid }) {
  return (
    <label className={`flex items-start gap-3 ${disabled ? "opacity-70" : "cursor-pointer"}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange && onChange(e.target.checked)}
        data-testid={testid}
        className="mt-1 accent-m-orange w-4 h-4 shrink-0"
      />
      <div>
        <div className="text-sm font-semibold text-m-navy">{label}</div>
        <div className="text-xs text-m-text-2 leading-relaxed">{desc}</div>
      </div>
    </label>
  );
}
